// Pure, so the schema, the v2 markdown reader and the importer all write the
// same markup without pulling Tiptap into the portal's render path.

export const FIELD_LOCATIONS = ["query", "path", "body", "header"] as const
export type FieldLocation = (typeof FIELD_LOCATIONS)[number]
export type FieldKind = "param" | "response"

export const FIELD_DATA_TYPE: Record<FieldKind, string> = {
	param: "param-field",
	response: "response-field",
}

export const FIELD_TAG: Record<FieldKind, string> = {
	param: "ParamField",
	response: "ResponseField",
}

export const DEFAULT_EXPANDABLE_TITLE = "properties"

export interface FieldAttributes {
	name: string
	location: FieldLocation | null
	fieldType: string
	required: boolean
	deprecated: boolean
	// The text the pill shows: a string default keeps its quotes, as on Mintlify.
	defaultValue: string | null
	placeholder: string | null
	pre: string[]
	post: string[]
}

export const emptyFieldAttributes = (
	kind: FieldKind,
	overrides: Partial<FieldAttributes> = {},
): FieldAttributes => ({
	name: "",
	location: kind === "param" ? "query" : null,
	fieldType: "string",
	required: false,
	deprecated: false,
	defaultValue: null,
	placeholder: null,
	pre: [],
	post: [],
	...overrides,
})

export const isFieldLocation = (value: unknown): value is FieldLocation =>
	typeof value === "string" &&
	(FIELD_LOCATIONS as readonly string[]).includes(value)

export function escapeHtml(value: string): string {
	return value
		.replace(/&/g, "&amp;")
		.replace(/"/g, "&quot;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
}

export function parseLabelList(value: string | null | undefined): string[] {
	if (!value) return []
	try {
		const parsed: unknown = JSON.parse(value)
		if (Array.isArray(parsed)) {
			return parsed.filter((item): item is string => typeof item === "string")
		}
		if (typeof parsed === "string") return [parsed]
	} catch {
		// A bare label, not a JSON array.
	}
	return [value]
}

/** A DOM spec Tiptap's renderHTML accepts; `0` marks where the body goes. */
export type HtmlSpec =
	| string
	| 0
	| [string, Record<string, string>, ...HtmlSpec[]]

const pill = (text: string): HtmlSpec => [
	"span",
	{ class: "pl-field-pill" },
	text,
]

export function fieldHeadSpec(
	kind: FieldKind,
	attrs: FieldAttributes,
): HtmlSpec {
	const meta: HtmlSpec[] = []
	if (attrs.fieldType) meta.push(pill(attrs.fieldType))
	if (kind === "param" && attrs.location) meta.push(pill(attrs.location))
	if (attrs.defaultValue !== null && attrs.defaultValue !== "") {
		meta.push([
			"span",
			{ class: "pl-field-pill" },
			["span", { class: "pl-field-pill-label" }, "default:"],
			["span", {}, attrs.defaultValue],
		])
	}
	if (attrs.required) {
		meta.push(["span", { class: "pl-field-required" }, "required"])
	}
	if (attrs.deprecated) {
		meta.push(["span", { class: "pl-field-deprecated" }, "deprecated"])
	}
	meta.push(...attrs.post.map(pill))

	return [
		"div",
		{ class: "pl-field-head", contenteditable: "false" },
		[
			"div",
			{ class: "pl-field-head-row" },
			...attrs.pre.map(pill),
			["span", { class: "pl-field-name" }, attrs.name],
			["span", { class: "pl-field-meta" }, ...meta],
		],
	]
}

export function fieldRootAttributes(
	kind: FieldKind,
	attrs: FieldAttributes,
): Record<string, string> {
	const out: Record<string, string> = {
		"data-type": FIELD_DATA_TYPE[kind],
		class: `pl-field pl-${FIELD_DATA_TYPE[kind]}`,
		"data-name": attrs.name,
		"data-field-type": attrs.fieldType,
	}
	if (kind === "param" && attrs.location) out["data-location"] = attrs.location
	if (attrs.required) out["data-required"] = ""
	if (attrs.deprecated) out["data-deprecated"] = ""
	if (attrs.defaultValue !== null) out["data-default"] = attrs.defaultValue
	if (kind === "param" && attrs.placeholder !== null) {
		out["data-placeholder"] = attrs.placeholder
	}
	if (attrs.pre.length) out["data-pre"] = JSON.stringify(attrs.pre)
	if (attrs.post.length) out["data-post"] = JSON.stringify(attrs.post)
	return out
}

export function readFieldAttributes(
	kind: FieldKind,
	element: Element,
): FieldAttributes {
	const location = element.getAttribute("data-location")
	return {
		name: element.getAttribute("data-name") ?? "",
		location:
			kind === "param" ? (isFieldLocation(location) ? location : null) : null,
		fieldType: element.getAttribute("data-field-type") ?? "",
		required: element.hasAttribute("data-required"),
		deprecated: element.hasAttribute("data-deprecated"),
		defaultValue: element.getAttribute("data-default"),
		placeholder:
			kind === "param" ? element.getAttribute("data-placeholder") : null,
		pre: parseLabelList(element.getAttribute("data-pre")),
		post: parseLabelList(element.getAttribute("data-post")),
	}
}

export function specToHtml(spec: HtmlSpec, bodyHtml: string): string {
	if (spec === 0) return bodyHtml
	if (typeof spec === "string") return escapeHtml(spec)
	const [tag, attributes, ...children] = spec
	const attributeText = Object.entries(attributes)
		.map(([key, value]) => ` ${key}="${escapeHtml(value)}"`)
		.join("")
	return `<${tag}${attributeText}>${children
		.map((child) => specToHtml(child, bodyHtml))
		.join("")}</${tag}>`
}

export function fieldSpec(kind: FieldKind, attrs: FieldAttributes): HtmlSpec {
	return [
		"div",
		fieldRootAttributes(kind, attrs),
		fieldHeadSpec(kind, attrs),
		["div", { class: "pl-field-body" }, 0],
	]
}

export function fieldHtml(
	kind: FieldKind,
	attrs: FieldAttributes,
	bodyHtml: string,
): string {
	// The schema needs one block in the body, so an empty field keeps a line.
	return specToHtml(fieldSpec(kind, attrs), bodyHtml.trim() || "<p></p>")
}

export function expandableSpec(title: string, defaultOpen: boolean): HtmlSpec {
	const root: Record<string, string> = {
		"data-type": "expandable",
		class: "pl-expandable",
		"data-title": title,
	}
	if (defaultOpen) root.open = ""
	return [
		"details",
		root,
		[
			"summary",
			{ class: "pl-expandable-summary", contenteditable: "false" },
			["span", { class: "pl-expandable-icon", "aria-hidden": "true" }],
			["span", { class: "pl-expandable-title" }, title],
		],
		["div", { class: "pl-expandable-content" }, 0],
	]
}

export function expandableHtml(
	title: string,
	defaultOpen: boolean,
	bodyHtml: string,
): string {
	return specToHtml(
		expandableSpec(title || DEFAULT_EXPANDABLE_TITLE, defaultOpen),
		bodyHtml.trim() || "<p></p>",
	)
}

// ---- Mintlify's MDX props ----

export type JsxAttributes = Record<string, string | true>

const JSX_ATTRIBUTE =
	/([A-Za-z_][\w-]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|\{((?:[^{}]|\{[^{}]*\})*)\}))?/g

/** Reads `name="x" required default={10}` into a map; a bare prop is `true`. */
export function parseJsxAttributes(raw: string): JsxAttributes {
	const out: JsxAttributes = {}
	for (const match of raw.matchAll(JSX_ATTRIBUTE)) {
		const key = match[1]
		if (!key) continue
		const quoted = match[2] ?? match[3]
		const expression = match[4]
		if (quoted !== undefined) out[key] = quoted
		else if (expression !== undefined) out[key] = `{${expression.trim()}}`
		else out[key] = true
	}
	return out
}

const unwrapExpression = (value: string): string | null =>
	value.startsWith("{") && value.endsWith("}")
		? value.slice(1, -1).trim()
		: null

export function jsxBoolean(value: string | true | undefined): boolean {
	if (value === undefined) return false
	if (value === true) return true
	const expression = unwrapExpression(value)
	if (expression !== null) return expression === "true"
	return value !== "false"
}

// Mintlify's markdown export escapes these inside attribute values: `ReactElement\<StepProps\>[]`.
const MARKDOWN_ESCAPE = /\\(&lt;|&gt;|&amp;|[<>[\]*_`\\])/g

export function jsxText(value: string | true | undefined): string | null {
	if (value === undefined || value === true) return null
	const expression = unwrapExpression(value)
	if (expression === null) return value.replace(MARKDOWN_ESCAPE, "$1")
	try {
		const parsed: unknown = JSON.parse(expression)
		if (typeof parsed === "string") return parsed
	} catch {
		// A non-JSON expression keeps its source text.
	}
	return expression
}

function jsxDefault(value: string | true | undefined): string | null {
	if (value === undefined || value === true) return null
	const expression = unwrapExpression(value)
	return expression === null ? JSON.stringify(value) : expression
}

function jsxLabels(value: string | true | undefined): string[] {
	if (value === undefined || value === true) return []
	const expression = unwrapExpression(value)
	if (expression === null) return [value]
	return parseLabelList(expression.replace(/'/g, '"'))
}

export function fieldAttributesFromJsx(
	kind: FieldKind,
	props: JsxAttributes,
): FieldAttributes {
	let name = jsxText(props.name) ?? ""
	let location: FieldLocation | null = null
	if (kind === "param") {
		for (const candidate of FIELD_LOCATIONS) {
			const value = jsxText(props[candidate])
			if (value !== null) {
				location = candidate
				name = value
				break
			}
		}
	}
	return {
		name,
		location,
		fieldType: jsxText(props.type) ?? "",
		required: jsxBoolean(props.required),
		deprecated: jsxBoolean(props.deprecated),
		defaultValue: jsxDefault(props.default),
		placeholder: kind === "param" ? jsxText(props.placeholder) : null,
		pre: jsxLabels(props.pre),
		post: jsxLabels(props.post),
	}
}

const quoteProp = (value: string): string =>
	value.includes('"') ? `{${JSON.stringify(value)}}` : `"${value}"`

// The pill text comes back to Mintlify's own form: a quoted string stays a
// string prop, a number or a list goes in braces.
function defaultProp(value: string): string {
	try {
		const parsed: unknown = JSON.parse(value)
		if (typeof parsed === "string") return `default=${quoteProp(parsed)}`
		return `default={${value}}`
	} catch {
		return `default=${quoteProp(value)}`
	}
}

const labelsProp = (key: string, labels: string[]): string =>
	`${key}={${JSON.stringify(labels)}}`

export function fieldJsxAttributes(
	kind: FieldKind,
	attrs: FieldAttributes,
): string {
	const parts: string[] = []
	if (kind === "param") {
		parts.push(`${attrs.location ?? "body"}=${quoteProp(attrs.name)}`)
	} else {
		parts.push(`name=${quoteProp(attrs.name)}`)
	}
	if (attrs.fieldType) parts.push(`type=${quoteProp(attrs.fieldType)}`)
	if (attrs.defaultValue !== null && attrs.defaultValue !== "") {
		parts.push(defaultProp(attrs.defaultValue))
	}
	if (kind === "param" && attrs.placeholder) {
		parts.push(`placeholder=${quoteProp(attrs.placeholder)}`)
	}
	if (attrs.pre.length) parts.push(labelsProp("pre", attrs.pre))
	if (attrs.post.length) parts.push(labelsProp("post", attrs.post))
	if (attrs.required) parts.push("required")
	if (attrs.deprecated) parts.push("deprecated")
	return parts.join(" ")
}

export function expandableJsxAttributes(
	title: string,
	defaultOpen: boolean,
): string {
	const parts = [`title=${quoteProp(title || DEFAULT_EXPANDABLE_TITLE)}`]
	if (defaultOpen) parts.push("defaultOpen")
	return parts.join(" ")
}

// ---- Balanced tag matching ----

export interface TagMatch {
	tag: string
	attributes: string
	body: string
	start: number
	end: number
	selfClosing: boolean
}

/**
 * Mintlify's container components. A field inside one belongs to that block's
 * own body parse, so the scan steps over it rather than lifting it out.
 */
export const MDX_CONTAINER_TAGS = [
	"ParamField",
	"ResponseField",
	"Expandable",
	"Accordion",
	"AccordionGroup",
	"Tabs",
	"Tab",
	"CodeGroup",
	"Card",
	"CardGroup",
	"Columns",
	"Column",
	"Steps",
	"Step",
	"Frame",
	"Note",
	"Info",
	"Tip",
	"Warning",
	"Check",
	"Danger",
	"Error",
	"Success",
	"Callout",
	"Update",
	"Tree",
	"Folder",
	"RequestExample",
	"ResponseExample",
]

// A tag inside a code sample is the sample, not a component.
const CODE_SPAN =
	/^[ \t]*(`{3,}|~{3,})[^\n]*\n[\s\S]*?^[ \t]*\1[ \t]*$|`[^`\n]+`/gm

function codeRanges(source: string): Array<[number, number]> {
	return [...source.matchAll(CODE_SPAN)].map((match) => [
		match.index ?? 0,
		(match.index ?? 0) + match[0].length,
	])
}

const OPEN_TAG_ATTRIBUTES = `(?:[^>"'{/]|"[^"]*"|'[^']*'|\\{(?:[^{}]|\\{[^{}]*\\})*\\}|/(?!>))*`

/**
 * Finds the outermost `<Tag>` blocks of the given names. Nesting is counted
 * across every container tag, so a ResponseField inside an Expandable inside a
 * ResponseField closes where it should whatever the indentation, and one that
 * sits inside another container is left to that container's parse.
 */
export function findTagBlocks(
	source: string,
	tags: string[],
	scopeTags: string[] = MDX_CONTAINER_TAGS,
): TagMatch[] {
	const names = [...new Set([...tags, ...scopeTags])].join("|")
	const token = new RegExp(
		`<(${names})(?=[\\s/>])(${OPEN_TAG_ATTRIBUTES})(/?)>|</(${names})\\s*>`,
		"g",
	)
	const matches: TagMatch[] = []
	const stack: string[] = []
	const code = codeRanges(source)
	let open: {
		tag: string
		attributes: string
		start: number
		bodyStart: number
	} | null = null
	for (const match of source.matchAll(token)) {
		const index = match.index ?? 0
		if (code.some(([from, to]) => index >= from && index < to)) continue
		const openTag = match[1]
		const closeTag = match[4]
		if (openTag) {
			const selfClosing = match[3] === "/"
			if (stack.length === 0 && tags.includes(openTag)) {
				if (selfClosing) {
					matches.push({
						tag: openTag,
						attributes: match[2] ?? "",
						body: "",
						start: index,
						end: index + match[0].length,
						selfClosing: true,
					})
					continue
				}
				open = {
					tag: openTag,
					attributes: match[2] ?? "",
					start: index,
					bodyStart: index + match[0].length,
				}
			}
			if (!selfClosing) stack.push(openTag)
			continue
		}
		if (!closeTag) continue
		const at = stack.lastIndexOf(closeTag)
		// A stray closing tag closes nothing.
		if (at < 0) continue
		stack.length = at
		if (stack.length === 0 && open && closeTag === open.tag) {
			matches.push({
				tag: open.tag,
				attributes: open.attributes,
				body: source.slice(open.bodyStart, index),
				start: open.start,
				end: index + match[0].length,
				selfClosing: false,
			})
			open = null
		}
		if (stack.length === 0) open = null
	}
	return matches
}
